'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function ZScoreCalculator() {
  const t = useTranslations("tools.zScoreCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Z-Score Calculator State
  const [zScoreInputs, setZScoreInputs] = useState({
    rawScore: '',
    mean: '',
    stdDev: ''
  });
  const [zScoreResults, setZScoreResults] = useState<any>(null);

  // Converter State
  const [converterInputs, setConverterInputs] = useState({
    zValue: '',
    probValue: ''
  });
  const [converterResults, setConverterResults] = useState<any>(null);

  // Range Calculator State
  const [rangeInputs, setRangeInputs] = useState({
    z1: '',
    z2: ''
  });
  const [rangeResults, setRangeResults] = useState<any>(null);

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  // Statistical functions
  const erf = (x: number): number => {
    const a1 = 0.254829592;
    const a2 = -0.284496736;
    const a3 = 1.421413741;
    const a4 = -1.453152027;
    const a5 = 1.061405429;
    const p = 0.3275911;

    const sign = x < 0 ? -1 : 1;
    x = Math.abs(x);

    const t = 1.0 / (1.0 + p * x);
    const y = 1.0 - (((((a5 * t + a4) * t) + a3) * t + a2) * t + a1) * t * Math.exp(-x * x);

    return sign * y;
  };

  const normalCDF = (z: number): number => {
    return 0.5 * (1 + erf(z / Math.sqrt(2)));
  };

  const inverseNormalCDF = (p: number): number | null => {
    if (p <= 0 || p >= 1) return null;

    const a = [0, -3.969683028665376e+01, 2.209460984245205e+02, -2.759285104469687e+02, 1.383577518672690e+02, -3.066479806614716e+01, 2.506628277459239e+00];
    const b = [0, -5.447609879822406e+01, 1.615858368580409e+02, -1.556989798598866e+02, 6.680131188771972e+01, -1.328068155288572e+01];
    const c = [0, -7.784894002430293e-03, -3.223964580411365e-01, -2.400758277161838e+00, -2.549732539343734e+00, 4.374664141464968e+00, 2.938163982698783e+00];
    const d = [0, 7.784695709041462e-03, 3.224671290700398e-01, 2.445134137142996e+00, 3.754408661907416e+00];

    const pLow = 0.02425;
    const pHigh = 1 - pLow;
    let q, r, x;

    if (p < pLow) {
      q = Math.sqrt(-2 * Math.log(p));
      x = (((((c[1] * q + c[2]) * q + c[3]) * q + c[4]) * q + c[5]) * q + c[6]) / ((((d[1] * q + d[2]) * q + d[3]) * q + d[4]) * q + 1);
    } else if (p <= pHigh) {
      q = p - 0.5;
      r = q * q;
      x = (((((a[1] * r + a[2]) * r + a[3]) * r + a[4]) * r + a[5]) * r + a[6]) * q / (((((b[1] * r + b[2]) * r + b[3]) * r + b[4]) * r + b[5]) * r + 1);
    } else {
      q = Math.sqrt(-2 * Math.log(1 - p));
      x = -(((((c[1] * q + c[2]) * q + c[3]) * q + c[4]) * q + c[5]) * q + c[6]) / ((((d[1] * q + d[2]) * q + d[3]) * q + d[4]) * q + 1);
    }

    return x;
  };

  // Calculate Z-Score
  const handleZScoreCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    const rawScore = parseFloat(zScoreInputs.rawScore);
    const mean = parseFloat(zScoreInputs.mean);
    const stdDev = parseFloat(zScoreInputs.stdDev);

    if (isNaN(rawScore) || isNaN(mean) || isNaN(stdDev)) {
      alert(t('errorInvalidInput'));
      return;
    }

    if (stdDev <= 0) {
      alert(t('errorStdDevPositive'));
      return;
    }

    const zScore = (rawScore - mean) / stdDev;
    const probLess = normalCDF(zScore);
    const probGreater = 1 - probLess;
    const probZero = Math.abs(normalCDF(zScore) - 0.5);

    let interpretation = '';
    const absZ = Math.abs(zScore);
    const direction = zScore >= 0 ? t('above') : t('below');

    if (absZ < 1) {
      interpretation = t('interpretationNormal', { zScore: absZ.toFixed(2), direction });
    } else if (absZ < 2) {
      interpretation = t('interpretationModerate', { zScore: absZ.toFixed(2), direction, level: zScore >= 0 ? t('high') : t('low') });
    } else if (absZ < 3) {
      interpretation = t('interpretationSignificant', { zScore: absZ.toFixed(2), direction, level: zScore >= 0 ? t('high') : t('low') });
    } else {
      interpretation = t('interpretationOutlier', { zScore: absZ.toFixed(2), direction });
    }

    setZScoreResults({
      zScore: zScore.toFixed(4),
      probLess: probLess.toFixed(6),
      probGreater: probGreater.toFixed(6),
      probZero: probZero.toFixed(6),
      interpretation
    });
  };

  // Convert Z-Score/Probability
  const handleConverterCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    const zValue = converterInputs.zValue ? parseFloat(converterInputs.zValue) : null;
    const probValue = converterInputs.probValue ? parseFloat(converterInputs.probValue) : null;

    if (zValue === null && probValue === null) {
      alert(t('errorConverterInput'));
      return;
    }

    let results: any = {};

    if (zValue !== null) {
      const pLess = normalCDF(zValue);
      const pGreater = 1 - pLess;
      const pZero = Math.abs(pLess - 0.5);
      const pInside = 2 * normalCDF(Math.abs(zValue)) - 1;
      const pOutside = 2 * (1 - normalCDF(Math.abs(zValue)));

      results = {
        zScore: zValue.toFixed(4),
        pLess: pLess.toFixed(6),
        pGreater: pGreater.toFixed(6),
        pZero: pZero.toFixed(6),
        pInside: pInside.toFixed(6),
        pOutside: pOutside.toFixed(6)
      };
    } else if (probValue !== null) {
      if (probValue <= 0 || probValue >= 1) {
        alert(t('errorProbabilityRange'));
        return;
      }

      const z = inverseNormalCDF(probValue);
      if (z === null) {
        alert(t('errorProbabilityRange'));
        return;
      }

      const pLess = probValue;
      const pGreater = 1 - probValue;
      const pZero = Math.abs(probValue - 0.5);
      const pInside = 2 * normalCDF(Math.abs(z)) - 1;
      const pOutside = 2 * (1 - normalCDF(Math.abs(z)));

      results = {
        zScore: z.toFixed(4),
        pLess: pLess.toFixed(6),
        pGreater: pGreater.toFixed(6),
        pZero: pZero.toFixed(6),
        pInside: pInside.toFixed(6),
        pOutside: pOutside.toFixed(6)
      };
    }

    setConverterResults(results);
  };

  // Calculate Range Probability
  const handleRangeCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    const z1 = parseFloat(rangeInputs.z1);
    const z2 = parseFloat(rangeInputs.z2);

    if (isNaN(z1) || isNaN(z2)) {
      alert(t('errorInvalidInput'));
      return;
    }

    if (z1 >= z2) {
      alert(t('errorRangeBounds'));
      return;
    }

    const prob1 = normalCDF(z1);
    const prob2 = normalCDF(z2);
    const rangeProbability = prob2 - prob1;
    const percentage = (rangeProbability * 100).toFixed(2);

    const interpretation = t('rangeInterpretation', { percentage, z1: z1.toFixed(2), z2: z2.toFixed(2) });

    setRangeResults({
      probability: rangeProbability.toFixed(6),
      percentage,
      interpretation
    });
  };

  const colors = {
    primary: '#2563eb',
    primaryHover: '#1d4ed8',
    secondary: '#7c3aed',
    accent: '#f59e0b',
    textPrimary: '#1e293b',
    textSecondary: '#475569',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(37, 99, 235, 0.2)',
    cardBg: '#ffffff',
    gradientStart: '#f0f9ff',
    gradientEnd: '#e0f2fe',
  };

  const baseStyles = {
    container: {
      maxWidth: '1200px',
      margin: '0 auto',
      position: 'relative' as const,
      zIndex: 1,
    },
    header: {
      textAlign: 'center' as const,
      marginBottom: '2rem',
    },
    h1: {
      fontWeight: 700,
      color: colors.textPrimary,
      marginBottom: '1rem',
      lineHeight: 1.2,
    },
    summaryText: {
      color: colors.textSecondary,
      lineHeight: 1.6,
    },
    calculatorTool: {
      background: `linear-gradient(135deg, ${colors.gradientStart} 0%, ${colors.gradientEnd} 100%)`,
      borderRadius: '1rem',
      marginBottom: '2rem',
    },
    toolContainer: {
      maxWidth: '100%',
      margin: '0 auto',
    },
    calculatorGrid: {
      display: 'grid',
      gap: '2rem',
      marginBottom: '2rem',
    },
    calculatorCard: {
      background: colors.cardBg,
      borderRadius: '1rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      transition: 'transform 0.2s, box-shadow 0.2s',
    },
    cardTitle: {
      fontWeight: 600,
      color: colors.primary,
      marginBottom: '1.5rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    formGroup: {
      marginBottom: '1.25rem',
    },
    label: {
      display: 'block',
      fontWeight: 500,
      color: colors.textSecondary,
      marginBottom: '0.5rem',
      fontSize: '0.95rem',
    },
    formulaText: {
      fontFamily: "'Courier New', monospace",
      fontWeight: 600,
      color: colors.secondary,
    },
    input: {
      width: '100%',
      border: `2px solid #e2e8f0`,
      borderRadius: '0.5rem',
      fontSize: '1rem',
      transition: 'border-color 0.2s, box-shadow 0.2s',
      boxSizing: 'border-box' as const,
    },
    calculateBtn: {
      width: '100%',
      background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
      color: 'white',
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'transform 0.2s, box-shadow 0.2s',
      marginTop: '0.5rem',
    },
    clearBtn: {
      width: '100%',
      padding: '0.75rem',
      background: '#f1f5f9',
      color: colors.textSecondary,
      border: 'none',
      borderRadius: '0.5rem',
      fontSize: '0.95rem',
      fontWeight: 500,
      cursor: 'pointer',
      transition: 'background 0.2s',
      marginTop: '0.5rem',
    },
    results: {
      background: colors.cardBg,
      borderRadius: '1rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
      marginTop: '2rem',
    },
    resultsH3: {
      color: colors.textPrimary,
      marginBottom: '1.5rem',
      fontWeight: 700,
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    resultGrid: {
      display: 'grid',
      gap: '1rem',
    },
    resultItem: {
      background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
      borderRadius: '0.75rem',
      borderLeft: `4px solid ${colors.primary}`,
    },
    resultItemHighlight: {
      background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
      borderLeft: `4px solid ${colors.accent}`,
    },
    resultLabel: {
      display: 'block',
      fontSize: '0.875rem',
      color: '#64748b',
      marginBottom: '0.5rem',
      fontWeight: 500,
    },
    resultValue: {
      display: 'block',
      color: colors.textPrimary,
      fontWeight: 700,
    },
    interpretation: {
      background: 'linear-gradient(135deg, #ede9fe 0%, #ddd6fe 100%)',
      borderRadius: '0.75rem',
      marginTop: '1.5rem',
      borderLeft: `4px solid ${colors.secondary}`,
    },
    interpretationTitle: {
      fontWeight: 600,
      color: '#5b21b6',
      marginBottom: '0.75rem',
      fontSize: '1.1rem',
    },
    interpretationText: {
      color: '#6b21a8',
      lineHeight: 1.6,
    },
    infoBox: {
      background: '#dbeafe',
      borderRadius: '0.5rem',
      marginTop: '1rem',
      borderLeft: `4px solid ${colors.primary}`,
      fontSize: '0.9rem',
      color: '#1e40af',
    },
    probabilityTable: {
      width: '100%',
      marginTop: '1rem',
      borderCollapse: 'collapse' as const,
    },
    glassCard: {
      background: colors.glassBg,
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: `1px solid ${colors.glassBorder}`,
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(37, 99, 235, 0.08)',
      marginBottom: '2rem',
    },
    h2: {
      fontWeight: 600,
      color: colors.textPrimary,
      marginBottom: '1rem',
    },
    paragraph: {
      lineHeight: 1.6,
      color: colors.textSecondary,
      marginBottom: '1rem',
    },
    list: {
      marginLeft: '1.5rem',
      lineHeight: 1.8,
      color: colors.textSecondary,
    },
    listItem: {
      marginBottom: '0.75rem',
    },
    strong: {
      fontWeight: 600,
      color: colors.textPrimary,
    },
    faqItem: {
      marginBottom: '1.5rem',
    },
    faqQuestion: {
      fontWeight: 600,
      color: colors.textPrimary,
      marginBottom: '0.5rem',
    },
    reference: {
      fontSize: '0.9rem',
      color: colors.textSecondary,
      marginBottom: '0.5rem',
      lineHeight: 1.5,
    },
    link: {
      color: colors.primary,
      textDecoration: 'none',
      wordBreak: 'break-word' as const,
    },
  };

  const containerStyle = getResponsiveStyle(
    baseStyles.container,
    { padding: '1rem' },
    { padding: '2rem' },
    { padding: '3rem' }
  );

  const headerStyle = getResponsiveStyle(
    baseStyles.header,
    { marginBottom: '2rem' },
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    baseStyles.h1,
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    baseStyles.summaryText,
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    baseStyles.calculatorTool,
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem' },
    { padding: '3rem 2rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    baseStyles.toolContainer,
    { maxWidth: '100%' },
    { maxWidth: '100%' },
    { maxWidth: '100%' }
  );

  const calculatorGridStyle = getResponsiveStyle(
    baseStyles.calculatorGrid,
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: 'repeat(2, 1fr)' },
    { gridTemplateColumns: 'repeat(3, 1fr)' }
  );

  const calculatorCardStyle = getResponsiveStyle(
    baseStyles.calculatorCard,
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const cardTitleStyle = getResponsiveStyle(
    baseStyles.cardTitle,
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.25rem' }
  );

  const inputStyle = getResponsiveStyle(
    baseStyles.input,
    { padding: '0.75rem' },
    { padding: '0.75rem' },
    { padding: '0.75rem' }
  );

  const calculateBtnStyle = getResponsiveStyle(
    baseStyles.calculateBtn,
    { padding: '0.875rem', fontSize: '1rem' },
    { padding: '0.875rem', fontSize: '1rem' },
    { padding: '0.875rem', fontSize: '1rem' }
  );

  const resultsStyle = getResponsiveStyle(
    baseStyles.results,
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const resultsH3Style = getResponsiveStyle(
    baseStyles.resultsH3,
    { fontSize: '1.25rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.5rem' }
  );

  const resultGridStyle = getResponsiveStyle(
    baseStyles.resultGrid,
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: 'repeat(2, 1fr)' },
    { gridTemplateColumns: 'repeat(2, 1fr)' }
  );

  const resultItemStyle = getResponsiveStyle(
    baseStyles.resultItem,
    { padding: '1rem' },
    { padding: '1.25rem' },
    { padding: '1.25rem' }
  );

  const resultItemHighlightStyle = getResponsiveStyle(
    baseStyles.resultItemHighlight,
    { padding: '1rem' },
    { padding: '1.25rem' },
    { padding: '1.25rem' }
  );

  const resultValueStyle = getResponsiveStyle(
    baseStyles.resultValue,
    { fontSize: '1.25rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.5rem' }
  );

  const interpretationStyle = getResponsiveStyle(
    baseStyles.interpretation,
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '1.5rem' }
  );

  const infoBoxStyle = getResponsiveStyle(
    baseStyles.infoBox,
    { padding: '0.875rem' },
    { padding: '1rem' },
    { padding: '1rem' }
  );

  const glassCardStyle = getResponsiveStyle(
    baseStyles.glassCard,
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2.5rem' }
  );

  const h2Style = getResponsiveStyle(
    baseStyles.h2,
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    baseStyles.paragraph,
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={summaryTextStyle}>{t('subtitle')}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={calculatorGridStyle}>
            <div style={calculatorCardStyle}>
              <h3 style={cardTitleStyle}>
                <span style={{ fontSize: '1.5rem' }}>📊</span>
                {t('calculateZScoreTitle')}
              </h3>
              <form onSubmit={handleZScoreCalculate}>
                <div style={baseStyles.formGroup}>
                  <label style={baseStyles.label} htmlFor="raw-score">
                    {t('rawScoreLabel')} (<span style={baseStyles.formulaText}>x</span>):
                  </label>
                  <input
                    type="number"
                    id="raw-score"
                    step="any"
                    placeholder={t('rawScorePlaceholder')}
                    value={zScoreInputs.rawScore}
                    onChange={(e) => setZScoreInputs({...zScoreInputs, rawScore: e.target.value})}
                    style={inputStyle}
                    required
                  />
                </div>
                <div style={baseStyles.formGroup}>
                  <label style={baseStyles.label} htmlFor="mean">
                    {t('meanLabel')} (<span style={baseStyles.formulaText}>μ</span>):
                  </label>
                  <input
                    type="number"
                    id="mean"
                    step="any"
                    placeholder={t('meanPlaceholder')}
                    value={zScoreInputs.mean}
                    onChange={(e) => setZScoreInputs({...zScoreInputs, mean: e.target.value})}
                    style={inputStyle}
                    required
                  />
                </div>
                <div style={baseStyles.formGroup}>
                  <label style={baseStyles.label} htmlFor="std-dev">
                    {t('stdDevLabel')} (<span style={baseStyles.formulaText}>σ</span>):
                  </label>
                  <input
                    type="number"
                    id="std-dev"
                    step="any"
                    placeholder={t('stdDevPlaceholder')}
                    value={zScoreInputs.stdDev}
                    onChange={(e) => setZScoreInputs({...zScoreInputs, stdDev: e.target.value})}
                    style={inputStyle}
                    required
                  />
                </div>
                <button type="submit" style={calculateBtnStyle}>
                  {t('calculateButton')}
                </button>
                <button
                  type="button"
                  style={baseStyles.clearBtn}
                  onClick={() => {
                    setZScoreInputs({ rawScore: '', mean: '', stdDev: '' });
                    setZScoreResults(null);
                  }}
                >
                  {t('clearButton')}
                </button>
              </form>
            </div>

            <div style={calculatorCardStyle}>
              <h3 style={cardTitleStyle}>
                <span style={{ fontSize: '1.5rem' }}>🔄</span>
                {t('converterTitle')}
              </h3>
              <form onSubmit={handleConverterCalculate}>
                <div style={baseStyles.formGroup}>
                  <label style={baseStyles.label} htmlFor="z-value">
                    {t('zValueLabel')} (<span style={baseStyles.formulaText}>Z</span>):
                  </label>
                  <input
                    type="number"
                    id="z-value"
                    step="any"
                    placeholder={t('zValuePlaceholder')}
                    value={converterInputs.zValue}
                    onChange={(e) => setConverterInputs({...converterInputs, zValue: e.target.value})}
                    style={inputStyle}
                  />
                </div>
                <div style={infoBoxStyle}>
                  {t('converterInfo')}
                </div>
                <div style={baseStyles.formGroup}>
                  <label style={baseStyles.label} htmlFor="prob-value">
                    {t.raw('probValueLabel')}
                  </label>
                  <input
                    type="number"
                    id="prob-value"
                    step="any"
                    min="0"
                    max="1"
                    placeholder={t('probValuePlaceholder')}
                    value={converterInputs.probValue}
                    onChange={(e) => setConverterInputs({...converterInputs, probValue: e.target.value})}
                    style={inputStyle}
                  />
                </div>
                <button type="submit" style={calculateBtnStyle}>
                  {t('convertButton')}
                </button>
                <button
                  type="button"
                  style={baseStyles.clearBtn}
                  onClick={() => {
                    setConverterInputs({ zValue: '', probValue: '' });
                    setConverterResults(null);
                  }}
                >
                  {t('clearButton')}
                </button>
              </form>
            </div>

            <div style={calculatorCardStyle}>
              <h3 style={cardTitleStyle}>
                <span style={{ fontSize: '1.5rem' }}>📏</span>
                {t('rangeTitle')}
              </h3>
              <form onSubmit={handleRangeCalculate}>
                <div style={baseStyles.formGroup}>
                  <label style={baseStyles.label} htmlFor="z1">
                    {t('leftBoundLabel')} (<span style={baseStyles.formulaText}>Z₁</span>):
                  </label>
                  <input
                    type="number"
                    id="z1"
                    step="any"
                    placeholder={t('leftBoundPlaceholder')}
                    value={rangeInputs.z1}
                    onChange={(e) => setRangeInputs({...rangeInputs, z1: e.target.value})}
                    style={inputStyle}
                    required
                  />
                </div>
                <div style={baseStyles.formGroup}>
                  <label style={baseStyles.label} htmlFor="z2">
                    {t('rightBoundLabel')} (<span style={baseStyles.formulaText}>Z₂</span>):
                  </label>
                  <input
                    type="number"
                    id="z2"
                    step="any"
                    placeholder={t('rightBoundPlaceholder')}
                    value={rangeInputs.z2}
                    onChange={(e) => setRangeInputs({...rangeInputs, z2: e.target.value})}
                    style={inputStyle}
                    required
                  />
                </div>
                <div style={infoBoxStyle}>
                  {t('rangeInfo')}
                </div>
                <button type="submit" style={calculateBtnStyle}>
                  {t('calculateProbabilityButton')}
                </button>
                <button
                  type="button"
                  style={baseStyles.clearBtn}
                  onClick={() => {
                    setRangeInputs({ z1: '', z2: '' });
                    setRangeResults(null);
                  }}
                >
                  {t('clearButton')}
                </button>
              </form>
            </div>
          </div>

          {zScoreResults && (
            <div style={resultsStyle}>
              <h3 style={resultsH3Style}>📊 {t('zScoreResultsTitle')}</h3>
              <div style={resultGridStyle}>
                <div style={resultItemHighlightStyle}>
                  <span style={baseStyles.resultLabel}>{t('zScoreLabel')}</span>
                  <span style={{...resultValueStyle, color: '#92400e'}}>{zScoreResults.zScore}</span>
                </div>
                <div style={resultItemStyle}>
                  <span style={baseStyles.resultLabel}>{t('probLessLabel')}</span>
                  <span style={resultValueStyle}>{zScoreResults.probLess}</span>
                </div>
                <div style={resultItemStyle}>
                  <span style={baseStyles.resultLabel}>{t('probGreaterLabel')}</span>
                  <span style={resultValueStyle}>{zScoreResults.probGreater}</span>
                </div>
                <div style={resultItemStyle}>
                  <span style={baseStyles.resultLabel}>{t('probZeroLabel')}</span>
                  <span style={resultValueStyle}>{zScoreResults.probZero}</span>
                </div>
              </div>
              <div style={interpretationStyle}>
                <div style={baseStyles.interpretationTitle}>{t('interpretationTitle')}</div>
                <div style={baseStyles.interpretationText}>{zScoreResults.interpretation}</div>
              </div>
            </div>
          )}

          {converterResults && (
            <div style={resultsStyle}>
              <h3 style={resultsH3Style}>🔄 {t('conversionResultsTitle')}</h3>
              <table style={baseStyles.probabilityTable}>
                <thead>
                  <tr>
                    <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #e2e8f0', background: '#f8fafc', fontWeight: 600, color: colors.textSecondary }}>{t('measureColumn')}</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #e2e8f0', background: '#f8fafc', fontWeight: 600, color: colors.textSecondary }}>{t('valueColumn')}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{ padding: '0.75rem', borderBottom: '1px solid #e2e8f0', color: colors.textPrimary }}><strong>{t('zScoreLabel')}</strong></td>
                    <td style={{ padding: '0.75rem', borderBottom: '1px solid #e2e8f0', color: colors.textPrimary }}>{converterResults.zScore}</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '0.75rem', borderBottom: '1px solid #e2e8f0', color: colors.textPrimary }}>{t('probLessLabel')}</td>
                    <td style={{ padding: '0.75rem', borderBottom: '1px solid #e2e8f0', color: colors.textPrimary }}>{converterResults.pLess}</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '0.75rem', borderBottom: '1px solid #e2e8f0', color: colors.textPrimary }}>{t('probGreaterLabel')}</td>
                    <td style={{ padding: '0.75rem', borderBottom: '1px solid #e2e8f0', color: colors.textPrimary }}>{converterResults.pGreater}</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '0.75rem', borderBottom: '1px solid #e2e8f0', color: colors.textPrimary }}>{t('probZeroRangeLabel')}</td>
                    <td style={{ padding: '0.75rem', borderBottom: '1px solid #e2e8f0', color: colors.textPrimary }}>{converterResults.pZero}</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '0.75rem', borderBottom: '1px solid #e2e8f0', color: colors.textPrimary }}>{t('probInsideLabel')}</td>
                    <td style={{ padding: '0.75rem', borderBottom: '1px solid #e2e8f0', color: colors.textPrimary }}>{converterResults.pInside}</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '0.75rem', borderBottom: '1px solid #e2e8f0', color: colors.textPrimary }}>{t('probOutsideLabel')}</td>
                    <td style={{ padding: '0.75rem', borderBottom: '1px solid #e2e8f0', color: colors.textPrimary }}>{converterResults.pOutside}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {rangeResults && (
            <div style={resultsStyle}>
              <h3 style={resultsH3Style}>📏 {t('rangeProbabilityResultsTitle')}</h3>
              <div style={resultGridStyle}>
                <div style={resultItemHighlightStyle}>
                  <span style={baseStyles.resultLabel}>{t('rangeProb')}</span>
                  <span style={{...resultValueStyle, color: '#92400e'}}>{rangeResults.probability}</span>
                </div>
                <div style={resultItemStyle}>
                  <span style={baseStyles.resultLabel}>{t('percentageLabel')}</span>
                  <span style={resultValueStyle}>{rangeResults.percentage}</span>
                </div>
              </div>
              <div style={interpretationStyle}>
                <div style={baseStyles.interpretationTitle}>{t('interpretationTitle')}</div>
                <div style={baseStyles.interpretationText}>{rangeResults.interpretation}</div>
              </div>
            </div>
          )}
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introParagraph1')}</p>
        <p style={paragraphStyle}>{t('introParagraph2')}</p>
        <p style={paragraphStyle}>{t('introParagraph3')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('howToUseTitle')}</h2>
        <ol style={baseStyles.list}>
          <li style={baseStyles.listItem}><strong style={baseStyles.strong}>{t('step1Title')}</strong> {t('step1Description')}</li>
          <li style={baseStyles.listItem}><strong style={baseStyles.strong}>{t('step2Title')}</strong> {t('step2Description')}</li>
          <li style={baseStyles.listItem}><strong style={baseStyles.strong}>{t('step3Title')}</strong> {t('step3Description')}</li>
          <li style={baseStyles.listItem}><strong style={baseStyles.strong}>{t('step4Title')}</strong> {t('step4Description')}</li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('insightsTitle')}</h2>
        <p style={paragraphStyle}>{t('insightsParagraph1')}</p>
        <ul style={baseStyles.list}>
          <li style={baseStyles.listItem}><strong style={baseStyles.strong}>{t('insight1Title')}</strong> {t('insight1Description')}</li>
          <li style={baseStyles.listItem}><strong style={baseStyles.strong}>{t('insight2Title')}</strong> {t('insight2Description')}</li>
          <li style={baseStyles.listItem}><strong style={baseStyles.strong}>{t('insight3Title')}</strong> {t('insight3Description')}</li>
          <li style={baseStyles.listItem}><strong style={baseStyles.strong}>{t('insight4Title')}</strong> {t('insight4Description')}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('detailsTitle')}</h2>
        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginTop: '1.5rem'}}>{t('formulaTitle')}</h3>
        <p style={paragraphStyle}>{t('formulaDescription')}</p>
        <div style={{ background: '#f8fafc', padding: '1rem', borderRadius: '0.5rem', marginBottom: '1rem', fontFamily: "'Courier New', monospace", fontSize: '1.125rem', textAlign: 'center' }}>
          Z = (X - μ) / σ
        </div>
        <p style={paragraphStyle}>{t('formulaExplanation')}</p>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginTop: '1.5rem'}}>{t('interpretationGuideTitle')}</h3>
        <ul style={baseStyles.list}>
          <li style={baseStyles.listItem}><strong style={baseStyles.strong}>{t('interpretation1Title')}</strong> {t('interpretation1Description')}</li>
          <li style={baseStyles.listItem}><strong style={baseStyles.strong}>{t('interpretation2Title')}</strong> {t('interpretation2Description')}</li>
          <li style={baseStyles.listItem}><strong style={baseStyles.strong}>{t('interpretation3Title')}</strong> {t('interpretation3Description')}</li>
          <li style={baseStyles.listItem}><strong style={baseStyles.strong}>{t('interpretation4Title')}</strong> {t('interpretation4Description')}</li>
        </ul>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginTop: '1.5rem'}}>{t('applicationsTitle')}</h3>
        <ul style={baseStyles.list}>
          <li style={baseStyles.listItem}>{t('application1')}</li>
          <li style={baseStyles.listItem}>{t('application2')}</li>
          <li style={baseStyles.listItem}>{t('application3')}</li>
          <li style={baseStyles.listItem}>{t('application4')}</li>
          <li style={baseStyles.listItem}>{t('application5')}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('faqTitle')}</h2>
        <div style={baseStyles.faqItem}>
          <h3 style={baseStyles.faqQuestion}>{t('faq1Question')}</h3>
          <p style={paragraphStyle}>{t('faq1Answer')}</p>
        </div>
        <div style={baseStyles.faqItem}>
          <h3 style={baseStyles.faqQuestion}>{t('faq2Question')}</h3>
          <p style={paragraphStyle}>{t('faq2Answer')}</p>
        </div>
        <div style={baseStyles.faqItem}>
          <h3 style={baseStyles.faqQuestion}>{t('faq3Question')}</h3>
          <p style={paragraphStyle}>{t('faq3Answer')}</p>
        </div>
        <div style={baseStyles.faqItem}>
          <h3 style={baseStyles.faqQuestion}>{t('faq4Question')}</h3>
          <p style={paragraphStyle}>{t('faq4Answer')}</p>
        </div>
        <div style={baseStyles.faqItem}>
          <h3 style={baseStyles.faqQuestion}>{t('faq5Question')}</h3>
          <p style={paragraphStyle}>{t('faq5Answer')}</p>
        </div>
        <div style={baseStyles.faqItem}>
          <h3 style={baseStyles.faqQuestion}>{t('faq6Question')}</h3>
          <p style={paragraphStyle}>{t('faq6Answer')}</p>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('referencesTitle')}</h2>
        <div style={baseStyles.reference}>
          1. <a href="https://www.geeksforgeeks.org/data-science/z-score-in-statistics/" target="_blank" rel="noopener noreferrer" style={baseStyles.link}>Z-Score in Statistics | Definition, Formula, Calculation and Uses - GeeksforGeeks</a>
        </div>
        <div style={baseStyles.reference}>
          2. <a href="https://www.simplypsychology.org/z-score.html" target="_blank" rel="noopener noreferrer" style={baseStyles.link}>Z-Score: Definition, Formula, Calculation & Interpretation - Simply Psychology</a>
        </div>
        <div style={baseStyles.reference}>
          3. <a href="https://statisticsbyjim.com/basics/z-score/" target="_blank" rel="noopener noreferrer" style={baseStyles.link}>Z-score: Definition, Formula, and Uses - Statistics By Jim</a>
        </div>
        <div style={baseStyles.reference}>
          4. <a href="https://articles.outlier.org/z-score-formula-examples-and-how-to-interpret" target="_blank" rel="noopener noreferrer" style={baseStyles.link}>Z-Score: Formula, Examples & How to Interpret It - Outlier Articles</a>
        </div>
        <div style={baseStyles.reference}>
          5. <a href="https://proclusacademy.com/blog/explainer/zscore-basics-practical-applications/" target="_blank" rel="noopener noreferrer" style={baseStyles.link}>Mastering Z-Scores: From Basics to Practical Applications - Proclus Academy</a>
        </div>
      </section>
    </div>
  );
}